import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../Constants';

function Profile() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      navigate('/login');
      return;
    }

    // Make the request to fetch profile data
    axios
      .get(`${BACKEND_URL}/api/dashboard/profile`, {
        headers: {
          Authorization: token,
        },
      })
      .then((response) => {
        setProfile(response.data);
        setEditedName(response.data.name); // Initialize edited name with current name
      })
      .catch((err) => {
        setError(err.response ? err.response.data.message : 'Error fetching profile');
      });
  }, [navigate]);

  const handleEditClick = () => {
    setIsEditing(true);
    setError(null);
    setSuccessMessage('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedName(profile.name); // Reset to original name
    setError(null);
    setSuccessMessage('');
  };

  const handleSaveEdit = async () => {
    const token = localStorage.getItem('token');
    
    if (!editedName.trim()) {
      setError('Name cannot be empty');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/dashboard/${profile._id}`,
        { name: editedName.trim() },
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json',
          },
        }
      );

      // Update the profile state with the new name
      setProfile({ ...profile, name: editedName.trim() });
      setIsEditing(false);
      setSuccessMessage('Name updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

    } catch (err) {
      setError(err.response ? err.response.data.message : 'Error updating profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (error && !profile) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          Error: {error}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading profile...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card">
            <div className="card-header">
              <h2 className="card-title mb-0">My Profile</h2>
            </div>
            <div className="card-body">
              {/* Success Message */}
              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  {successMessage}
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError(null)}
                    aria-label="Close"
                  ></button>
                </div>
              )}

              {/* Name Field */}
              <div className="mb-3">
                <label className="form-label fw-bold">Name:</label>
                {!isEditing ? (
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="form-control-plaintext">{profile.name}</span>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleEditClick}
                    >
                      Edit
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      className="form-control mb-2"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      placeholder="Enter your name"
                      disabled={isLoading}
                    />
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleSaveEdit}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleCancelEdit}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Email Field (Read-only) */}
              <div className="mb-3">
                <label className="form-label fw-bold">Email:</label>
                <div className="form-control-plaintext">{profile.email}</div>
              </div>

              {/* Role Field (Read-only) */}
              {profile.role && (
                <div className="mb-3">
                  <label className="form-label fw-bold">Role:</label>
                  <div className="form-control-plaintext text-capitalize">{profile.role}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;